import React, { useState } from 'react';
import { Box, Text, Input, HStack, VStack, Button, Popover, PopoverTrigger, PopoverContent, PopoverBody, PopoverArrow, PopoverCloseButton, Checkbox, Tag, TagLabel, TagCloseButton, NumberInput, NumberInputField, InputGroup, InputLeftElement, Tabs, TabList, TabPanels, Tab, TabPanel } from '@chakra-ui/react';
import PageTitle from '../components/PageTitle';
import { POSITIONS } from '../constants';
import { useMyLeagueStore } from '../store/myLeagueStore';
import { useWaivedPlayers } from '../hooks/useWaivedPlayers';
import AvailablePlayersContent from '../components/AvailablePlayersContent';
import { Spinner, Center } from '@chakra-ui/react';
import { positionBucketTokens } from '../utils/formatPositionsList';

// TODO: If needed, create a separate WaivedPlayerContent component

type Chip = { type: 'nameLike' | 'position' | 'salaryMin' | 'salaryMax' | 'status'; label: string; value?: string };

const WaiverWire: React.FC = () => {
  // Filter state
  const [nameLike, setNameLike] = useState('');
  const [selectedPositions, setSelectedPositions] = useState<string[]>([]);
  const [salaryMin, setSalaryMin] = useState<string>('');
  const [salaryMax, setSalaryMax] = useState<string>('');
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [positionPopoverOpen, setPositionPopoverOpen] = useState(false);
  const [statusPopoverOpen, setStatusPopoverOpen] = useState(false);

  // Get current league
  const myLeague = useMyLeagueStore(state => state.myLeague);
  const leagueId = myLeague?.id;

  // Note: Status filtering is now handled on the frontend

  // Load waived players
  const {
    data: waivedPlayersData,
    isLoading: waivedPlayersLoading,
    error: waivedPlayersError,
    refetch: refetchWaivedPlayers,
  } = useWaivedPlayers({
    leagueId,
    filters: {
      salaryMin,
      salaryMax,
      // Name, position, and status filtering now handled on frontend
    },
    page: 0,
    pageSize: 50,
  });

  // Apply frontend filtering (name, status, and position)
  const filteredPlayers = React.useMemo(() => {
    if (!waivedPlayersData?.players && !waivedPlayersData) return [];
    
    const players = waivedPlayersData?.players || waivedPlayersData || [];
    
    return players.filter((player: any) => {
      // Name filtering
      if (nameLike && nameLike.trim()) {
        const firstName = player.rwPlayer?.first_name || '';
        const lastName = player.rwPlayer?.last_name || '';
        const fullName = `${firstName} ${lastName}`.toLowerCase();
        const searchTerm = nameLike.toLowerCase().trim();
        
        if (!fullName.includes(searchTerm)) {
          return false;
        }
      }
      
      // Position filtering (rw_player_positions buckets)
      if (selectedPositions.length > 0) {
        const playerPositions = positionBucketTokens(player);
        const hasMatchingPosition = selectedPositions.some((selectedPos) =>
          playerPositions.some(
            (playerPos) => playerPos.toLowerCase() === selectedPos.toLowerCase()
          )
        );
        if (!hasMatchingPosition) return false;
      }
      
      // Status filtering
      if (selectedStatuses.length > 0) {
        const hasClaims = player.dealData && player.dealData.claims && player.dealData.claims > 0;
        
        if (selectedStatuses.includes('has_claims') && selectedStatuses.includes('no_claims')) {
          return true; // Show all if both are selected
        } else if (selectedStatuses.includes('has_claims')) {
          return hasClaims;
        } else if (selectedStatuses.includes('no_claims')) {
          return !hasClaims;
        }
      }
      
      return true;
    });
  }, [waivedPlayersData, selectedStatuses, selectedPositions, nameLike]);

  // Remove a filter chip
  const removeChip = (type: string, value?: string) => {
    if (type === 'nameLike') setNameLike('');
    if (type === 'position' && value) setSelectedPositions(selectedPositions.filter(p => p !== value));
    if (type === 'salaryMin') setSalaryMin('');
    if (type === 'salaryMax') setSalaryMax('');
    if (type === 'status' && value) setSelectedStatuses(selectedStatuses.filter(s => s !== value));
  };

  // Position multi-select logic
  const togglePosition = (pos: string) => {
    setSelectedPositions(prev => prev.includes(pos) ? prev.filter(p => p !== pos) : [...prev, pos]);
  };

  // Status multi-select logic
  const toggleStatus = (status: string) => {
    setSelectedStatuses(prev => prev.includes(status) ? prev.filter(s => s !== status) : [...prev, status]);
  };

  // Format salary with commas for chips and display
  const formatWithCommas = (val: string) => {
    const num = Number(val.replace(/,/g, ''));
    if (!val || isNaN(num)) return '';
    return num.toLocaleString();
  };

  // Render filter chips
  const chips: Chip[] = [
    nameLike ? { type: 'nameLike', label: `Name: ${nameLike}` } : undefined,
    ...selectedPositions.map(pos => ({ type: 'position', label: POSITIONS.find(p => p.value === pos)?.label || pos, value: pos })),
    salaryMin ? { type: 'salaryMin', label: `Min Salary: $${formatWithCommas(salaryMin)}` } : undefined,
    salaryMax ? { type: 'salaryMax', label: `Max Salary: $${formatWithCommas(salaryMax)}` } : undefined,
    ...selectedStatuses.map(status => ({ type: 'status', label: status === 'has_claims' ? 'Has Claims' : 'No Claims', value: status })),
  ].filter((chip): chip is Chip => Boolean(chip));

  return (
    <Box p={8}>
      <PageTitle mb={4}>Waiver Wire</PageTitle>
      {/* Filter Bar */}
      <VStack align="flex-start" spacing={4} mb={2}>
        <HStack spacing={4}>
          {/* Name Like */}
          <Input
            placeholder="Search by name..."
            value={nameLike}
            onChange={e => setNameLike(e.target.value)}
            width="220px"
            bg="whiteAlpha.100"
            color="white"
            _placeholder={{ color: 'gray.400' }}
          />
          {/* Position Multi-Select */}
          <Popover
            isOpen={positionPopoverOpen}
            onClose={() => setPositionPopoverOpen(false)}
            placement="bottom-start"
          >
            <PopoverTrigger>
              <Button onClick={() => setPositionPopoverOpen(!positionPopoverOpen)} variant="outline" colorScheme="blue">
                {selectedPositions.length > 0 ? `${selectedPositions.length} Position${selectedPositions.length > 1 ? 's' : ''}` : 'Select Positions'}
              </Button>
            </PopoverTrigger>
            <PopoverContent bg="gray.800" color="white" minW="200px">
              <PopoverArrow />
              <PopoverCloseButton />
              <PopoverBody>
                <VStack align="flex-start" spacing={1}>
                  {POSITIONS.map(pos => (
                    <Checkbox
                      key={pos.value}
                      isChecked={selectedPositions.includes(pos.value)}
                      onChange={() => togglePosition(pos.value)}
                      colorScheme="blue"
                    >
                      {pos.label}
                    </Checkbox>
                  ))}
                </VStack>
              </PopoverBody>
            </PopoverContent>
          </Popover>
          {/* Salary Range */}
          <HStack spacing={1}>
            <InputGroup>
              <InputLeftElement pointerEvents="none" color="gray.400" fontSize="md" children="$" />
              <NumberInput
                value={formatWithCommas(salaryMin)}
                onChange={val => setSalaryMin(val.replace(/,/g, ''))}
                min={0}
                width="170px"
                bg="whiteAlpha.100"
                color="white"
              >
                <NumberInputField placeholder="Min Salary" _placeholder={{ color: 'gray.400' }} pl={6} />
              </NumberInput>
            </InputGroup>
            <Text color="gray.400">-</Text>
            <InputGroup>
              <InputLeftElement pointerEvents="none" color="gray.400" fontSize="md" children="$" />
              <NumberInput
                value={formatWithCommas(salaryMax)}
                onChange={val => setSalaryMax(val.replace(/,/g, ''))}
                min={0}
                width="170px"
                bg="whiteAlpha.100"
                color="white"
              >
                <NumberInputField placeholder="Max Salary" _placeholder={{ color: 'gray.400' }} pl={6} />
              </NumberInput>
            </InputGroup>
          </HStack>
          {/* Status Multi-Select */}
          <Popover
            isOpen={statusPopoverOpen}
            onClose={() => setStatusPopoverOpen(false)}
            placement="bottom-start"
          >
            <PopoverTrigger>
              <Button onClick={() => setStatusPopoverOpen(!statusPopoverOpen)} variant="outline" colorScheme="blue">
                {selectedStatuses.length > 0 ? `${selectedStatuses.length} Status${selectedStatuses.length > 1 ? 'es' : ''}` : 'Select Status'}
              </Button>
            </PopoverTrigger>
            <PopoverContent bg="gray.800" color="white" minW="200px">
              <PopoverArrow />
              <PopoverCloseButton />
              <PopoverBody>
                <VStack align="flex-start" spacing={1}>
                  <Checkbox
                    isChecked={selectedStatuses.includes('has_claims')}
                    onChange={() => toggleStatus('has_claims')}
                    colorScheme="blue"
                  >
                    Has Claims
                  </Checkbox>
                  <Checkbox
                    isChecked={selectedStatuses.includes('no_claims')}
                    onChange={() => toggleStatus('no_claims')}
                    colorScheme="blue"
                  >
                    No Claims
                  </Checkbox>
                </VStack>
              </PopoverBody>
            </PopoverContent>
          </Popover>
        </HStack>
        {/* Filter Chips */}
        <HStack spacing={2} flexWrap="wrap">
          {chips.map((chip, idx) => (
            <Tag key={idx} size="md" colorScheme="blue" borderRadius="full">
              <TagLabel>{chip.label}</TagLabel>
              <TagCloseButton onClick={() => removeChip(chip.type, chip.value)} />
            </Tag>
          ))}
        </HStack>
      </VStack>
      {/* Tabs (same style as RosterPage) */}
      <Tabs 
        colorScheme="blue" 
        variant="enclosed" 
        mt={8}
        onChange={() => {
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              window.dispatchEvent(new Event('scroll'));
            });
          });
        }}
      >
        <TabList>
          <Tab sx={{ borderBottomRadius: 0 }}>Players</Tab>
          <Tab sx={{ borderBottomRadius: 0 }}>Additional Info</Tab>
          <Tab sx={{ borderBottomRadius: 0 }}>Contract Details</Tab>
        </TabList>
        <TabPanels>
          <TabPanel p={0}>
            {waivedPlayersLoading ? (
              <Center py={10}><Spinner size="xl" /></Center>
            ) : waivedPlayersError ? (
              <Text color="red.400">Error loading waived players.</Text>
            ) : leagueId ? (
              <AvailablePlayersContent
                tabName="Players"
                players={filteredPlayers}
                isWaiverWireView
                refetch={refetchWaivedPlayers}
              />
            ) : (
              <Text color="gray.400">Select a league to view waived players.</Text>
            )}
          </TabPanel>
          <TabPanel p={0}>
            {waivedPlayersLoading ? (
              <Center py={10}><Spinner size="xl" /></Center>
            ) : waivedPlayersError ? (
              <Text color="red.400">Error loading waived players.</Text>
            ) : leagueId ? (
              <AvailablePlayersContent
                tabName="Additional Info"
                players={filteredPlayers}
                isWaiverWireView
                refetch={refetchWaivedPlayers}
              />
            ) : (
              <Text color="gray.400">Select a league to view waived players.</Text>
            )}
          </TabPanel>
          <TabPanel p={0}>
            {waivedPlayersLoading ? (
              <Center py={10}><Spinner size="xl" /></Center>
            ) : waivedPlayersError ? (
              <Text color="red.400">Error loading waived players.</Text>
            ) : leagueId ? (
              <AvailablePlayersContent
                tabName="Contract Details"
                players={filteredPlayers}
                isWaiverWireView
                refetch={refetchWaivedPlayers}
              />
            ) : (
              <Text color="gray.400">Select a league to view waived players.</Text>
            )}
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Box>
  );
};

export default WaiverWire; 